import React, { createContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../services/BaseService';
import { doLogin, doLoginApp, doLoginFornecedor, doLogout } from '../../services/AuthService';
import { toast } from 'react-toastify';
import { setToken } from '../helpers/Api';
import { getEmpresaById } from '../../services/EmpresaService';
import { limparTodosRascunhos } from '../../utils/RascunhoMesaUtils';
import { primeiraRotaPermitida } from '../../utils/rotaInicialHelper';

const Context = createContext();

const AuthContext = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(!!sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(false);
  const [usuarioConsumidor, setUsuarioConsumidor] = useState(
    sessionStorage.getItem('consumerUser') ? JSON.parse(sessionStorage.getItem('consumerUser')) : ''
  );
  const [enderecoUsuarioConsumidor, setEnderecoUsuarioConsumidor] = useState('');
  const [searchingUrl, setSearchingUrl] = useState(true);
  const [cobrancaBloqueada, setCobrancaBloqueada] = useState(null);

  const [permissoes, setPermissoes] = useState(
    sessionStorage.getItem('sistemacpermissoes')
      ? JSON.parse(sessionStorage.getItem('sistemacpermissoes'))
      : []
  );

  // --- RESTAURA SESSÃO "LEMBRAR USUÁRIO" DO APP DO GARÇOM ---
  // Se o garçom marcou "Manter conectado", os dados ficam em localStorage.
  // Ao recarregar a página ou abrir uma nova aba, trazemos de volta para o
  // sessionStorage para o resto do sistema continuar funcionando igual.
  useEffect(() => {
    const appToken = localStorage.getItem('app_token');
    if (!appToken || sessionStorage.getItem('token')) return;

    const sessionKeys = [
      'token',
      'tipo_usuario',
      'cod_empresa',
      'cod_usuario',
      'sistemacemail',
      'sistemacefornecedor',
      'sistemacpermissoes',
      'login_origem',
      'funcao_funcionario',
      'empresa',
      'cod_org',
      'nfce',
    ];

    sessionKeys.forEach((key) => {
      const value = localStorage.getItem(`app_${key}`);
      if (value !== null) sessionStorage.setItem(key, value);
    });

    const codOrg = localStorage.getItem('app_cod_org');
    if (codOrg) setToken(appToken, codOrg);

    const perms = sessionStorage.getItem('sistemacpermissoes');
    setPermissoes(perms ? JSON.parse(perms) : []);
    setIsAuthenticated(appToken);
  }, []);
  // -----------------------------------------------------------

  // --- CRONÔMETRO DE INATIVIDADE (ENTERPRISE / MULTI-ABA) ---
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const SYNC_KEY = 'sistemac_last_activity'; // Chave para avisar as outras abas

  useEffect(() => {
    // Quando o login veio com "Manter conectado" (token de 7 dias), não faz
    // sentido deslogar por inatividade — o próprio logout manual é o controle.
    const loginOrigem = sessionStorage.getItem('login_origem');
    if (loginOrigem === 'app-lembrar') return;

    // 12 horas em milissegundos. (Troque para 60000 para testar com 1 minuto, deixe 43200000 para 12 horas)
    const TEMPO_INATIVIDADE = 43200000;
    //const TEMPO_INATIVIDADE = 60000;

    // isFromOtherTab = true evita um loop infinito de abas escrevendo no localStorage
    const resetTimer = (isFromOtherTab = false) => {
      const now = Date.now();

      // THROTTLE: Só atualiza se passou 5s da última vez, EXCETO se o aviso veio de outra aba
      if (!isFromOtherTab && timeoutRef.current && now - lastActivityRef.current < 5000) {
        return;
      }

      lastActivityRef.current = now;
      clearTimeout(timeoutRef.current);

      // Se a ação foi nesta aba, avisa as outras abas salvando no localStorage
      if (!isFromOtherTab) {
        localStorage.setItem(SYNC_KEY, now.toString());
      }

      // Reinicia o cronômetro fatal
      timeoutRef.current = setTimeout(() => {
        if (sessionStorage.getItem('token')) {
          forceLogout('Sessão expirada por inatividade. Faça login novamente.');
        }
      }, TEMPO_INATIVIDADE);
    };

    // Função que capta movimentos nesta tela
    const handleActivity = () => resetTimer(false);

    // Função que escuta se alguma outra aba avisou que o usuário está vivo lá
    const handleStorageSync = (e) => {
      if (e.key === SYNC_KEY) {
        resetTimer(true); // Atualiza o relógio desta aba silenciosamente
      }
    };

    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('click', handleActivity, { passive: true });
    window.addEventListener('storage', handleStorageSync); // Ouve outras abas

    resetTimer(false); // Disparo inicial

    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('storage', handleStorageSync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // -----------------------------------------------------------

  const temAcesso = (codigoOpcao) => {
    const tipo = sessionStorage.getItem('tipo_usuario');
    if (String(tipo).toUpperCase() === 'ADM' || String(tipo).toUpperCase() === 'ADMIN') return true;

    // Fallback temporário: durante a transição de PedidoMesa → permissões
    // específicas, usuários que ainda tenham apenas a permissão antiga
    // continuam conseguindo acessar as três novas funções.
    if (permissoes.includes(codigoOpcao)) return true;

    const permissaoLegada = 'PedidoMesa';
    if (!permissoes.includes(permissaoLegada)) return false;

    const filhosDePedidoMesa = new Set(['PedidosAtendimento', 'GestorCardapio', 'MesasAbertas']);
    return filhosDePedidoMesa.has(codigoOpcao);
  };

  function forceLogout(motivo = 'Sua empresa foi bloqueada.') {
    if (!sessionStorage.getItem('token')) return;

    try {
      setIsAuthenticated(null);

      // 1. Tenta pegar o slug do sessionStorage (verifique se a chave é 'empresa' mesmo)
      let empresaSlug = sessionStorage.getItem('empresa');

      // 2. BACKUP: Se o storage falhar, tenta pegar o primeiro segmento da URL atual
      // Ex: de "/sistemac/app/mesas", o split pega "sistemac"
      if (!empresaSlug) {
        const pathSegments = window.location.pathname.split('/');
        empresaSlug = pathSegments[1]; // O primeiro segmento após a /
      }

      const origem = sessionStorage.getItem('login_origem');

      // Define a base dependendo de onde o usuário veio
      const baseLoginPath =
        origem === 'app-pdv'
          ? '/app/pdv/auth/login'
          : origem === 'app' || origem === 'app-lembrar'
            ? '/app/auth/login'
            : '/auth/login';

      // Monta a URL final garantindo o slug
      // Se o slug for algo como "app" ou "auth" (segmentos reservados), tratamos como vazio
      const reservedKeywords = ['app', 'auth', 'painelAdm', 'dashboard'];
      const finalSlug =
        empresaSlug && !reservedKeywords.includes(empresaSlug) ? `/${empresaSlug}` : '';

      const urlDestino = `${finalSlug}${baseLoginPath}`;

      // Limpeza dos dados
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('sistemacemail');
      sessionStorage.removeItem('sistemacefornecedor');
      sessionStorage.removeItem('sistemacpermissoes');
      sessionStorage.removeItem('login_origem');

      // Limpa também o "Manter conectado" do app do garçom
      const appKeys = [
        'app_token',
        'app_tipo_usuario',
        'app_cod_empresa',
        'app_cod_usuario',
        'app_sistemacemail',
        'app_sistemacefornecedor',
        'app_sistemacpermissoes',
        'app_login_origem',
        'app_funcao_funcionario',
        'app_empresa',
        'app_cod_org',
        'app_nfce',
      ];
      appKeys.forEach((key) => localStorage.removeItem(key));

      toast.error(motivo);

      // Navegação SPA imediata (igual ao PainelLayout.jsx) em vez de hard reload
      // com delay — evita cair no fallback de SearchUrl (que rendia "Página não
      // encontrada 404") enquanto isAuthenticated/cod_org ainda estavam se
      // propagando. O toast sobrevive à troca de rota porque o ToastContainer
      // fica fora da árvore de rotas (ver index.js).
      navigate(urlDestino, { replace: true });
    } finally {
      sessionStorage.removeItem('cod_empresa');
      sessionStorage.removeItem('cod_usuario');
      sessionStorage.removeItem('cod_org');
      sessionStorage.removeItem('tipo_usuario');
      sessionStorage.removeItem('funcao_funcionario');
      setPermissoes([]);
      // Evita vazar rascunho de mesa abandonado pro próximo usuário do mesmo dispositivo
      limparTodosRascunhos();
    }
  }

  // NOVO: Adicionamos o parâmetro "origem" com 'web' como padrão para não quebrar o login antigo
  async function handleLogin(email, senha, isSupplier, cod_empresa_selecionada, origem = 'web') {
    setLoading(true);
    const cod_org = sessionStorage.getItem('cod_org');
    const loginFunction = isSupplier ? doLoginFornecedor : doLogin;

    try {
      const res = await loginFunction(email, senha, cod_org, cod_empresa_selecionada);

      if (res.status === 200) {
        const {
          token,
          tipo_usuario,
          ramo,
          cod_empresa,
          cod_usuario,
          situacao_empresa,
          permissoes: listaPermissoes,
          cod_pk_funcionario,
          funcao_funcionario,
          nfce,
        } = res.data;

        // VERIFICAÇÃO DE PAPÉIS:
        const tipoFormatado = String(tipo_usuario || '').toUpperCase();
        const isSuperAdmin = tipoFormatado === 'ADM'; // Apenas a equipe Dev (Acesso total e Painel Adm)
        const isStoreAdmin = tipoFormatado === 'ADMINISTRADOR'; // Donos das lojas
        const isVendedor = tipoFormatado === 'VENDEDOR';
        const isGerente = tipoFormatado === 'GERENTE';
        // 'ADMIN' e o super admin que o backend usa pra "logar na org do
        // cliente" (authController.js) ignorando empresa inativa/suspensa —
        // separado de isSuperAdmin pra nao mudar o redirecionamento (essa
        // conta continua caindo no dashboard normal da empresa escolhida,
        // nao no /painelAdm).
        const ignoraBloqueioEmpresa = isSuperAdmin || tipoFormatado === 'ADMIN';

        // A REGRA DE NEGÓCIO DO APP:
        // Permite o acesso se for App E (SuperAdmin OU Dono da Loja OU Vendedor OU Gerente OU tiver funcionário)
        const isOrigemApp = origem === 'app' || origem === 'app-pdv';
        if (
          isOrigemApp &&
          !isSuperAdmin &&
          !isStoreAdmin &&
          !isVendedor &&
          !isGerente &&
          !cod_pk_funcionario
        ) {
          toast.error(
            'Acesso negado: Apenas funcionários, administradores, gerentes ou vendedores podem acessar o App.'
          );
          setLoading(false);
          return;
        }

        sessionStorage.setItem('tipo_usuario', tipo_usuario || '');
        sessionStorage.setItem('nfce', nfce || 'NAO');

        // Apenas o Super Admin ignora o bloqueio de empresa inativa ou suspensa
        if (!ignoraBloqueioEmpresa) {
          let situacao = (situacao_empresa || '').toUpperCase();
          if (situacao === 'INATIVO' || situacao === 'SUSPENSO') {
            return forceLogout('Empresa bloqueada. Entre em contato com o suporte.');
          }
        }

        const listaFinal = listaPermissoes || [];
        sessionStorage.setItem('sistemacpermissoes', JSON.stringify(listaFinal));
        setPermissoes(listaFinal);

        sessionStorage.setItem('cod_empresa', cod_empresa || cod_empresa_selecionada);
        sessionStorage.setItem('cod_usuario', cod_usuario);
        sessionStorage.setItem('sistemacemail', email);
        sessionStorage.setItem('sistemacefornecedor', isSupplier ? 'sim' : 'nao');
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('login_origem', origem);
        sessionStorage.setItem('funcao_funcionario', funcao_funcionario || '');

        setToken(token, cod_org);
        toast.success('Login efetuado com sucesso');
        setIsAuthenticated(token);

        // temAcesso lê o state anterior ao login; no redirecionamento usa a lista recém-recebida.
        const temAcessoLogin = (codigo) =>
          tipoFormatado === 'ADMIN' || listaFinal.includes(codigo);

        setTimeout(() => {
          // REDIRECIONAMENTO CORRIGIDO:
          if (isSuperAdmin) {
            // SOMENTE a sua equipe de devs (ADM) vai para cá
            window.location.href = '/painelAdm';
          } else {
            // As Lojas (inclusive os 'ADMINISTRADOR') caem no fluxo normal
            if (origem === 'app') {
              window.location.href = '/app/mesas';
            } else if (origem === 'app-pdv') {
              window.location.href = '/app/pdv';
            } else if (ramo === 'BAR' && temAcessoLogin('PedidosAtendimento')) {
              window.location.href = '/pedidos';
            } else if (ramo === 'AUTOPECAS' && temAcessoLogin('PedidosAtendimento')) {
              window.location.href = '/painel-autopecas';
            } else if (temAcessoLogin('FDashboard')) {
              window.location.href = '/dashboard';
            } else {
              window.location.href = primeiraRotaPermitida(listaFinal);
            }
          }
        }, 500);
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 403 && data?.cobranca) {
        setCobrancaBloqueada(data.cobranca);
      } else {
        toast.error(data?.message || 'Erro inesperado.');
      }
    } finally {
      setLoading(false);
    }
  }

  // Login exclusivo da rota do garçom web (/app/auth/login).
  // Quando `lembrar` for true, persiste em localStorage para que o usuário
  // não precise digitar usuário/senha ao reabrir o app no tablet/celular.
  async function handleLoginApp(email, senha, isSupplier, cod_empresa_selecionada, lembrar = false) {
    setLoading(true);
    const cod_org = sessionStorage.getItem('cod_org');

    try {
      const res = await doLoginApp(email, senha, cod_org, cod_empresa_selecionada, lembrar);

      if (res.status === 200) {
        const {
          token,
          tipo_usuario,
          ramo,
          cod_empresa,
          cod_usuario,
          situacao_empresa,
          permissoes: listaPermissoes,
          cod_pk_funcionario,
          funcao_funcionario,
          nfce,
        } = res.data;

        const tipoFormatado = String(tipo_usuario || '').toUpperCase();
        const isSuperAdmin = tipoFormatado === 'ADM' || tipoFormatado === 'ADMIN';
        const isStoreAdmin = tipoFormatado === 'ADMINISTRADOR';
        const isVendedor = tipoFormatado === 'VENDEDOR';
        const isGerente = tipoFormatado === 'GERENTE';

        // Mesma regra de acesso do handleLogin, mas garantida no backend também.
        if (
          !isSuperAdmin &&
          !isStoreAdmin &&
          !isVendedor &&
          !isGerente &&
          !cod_pk_funcionario
        ) {
          toast.error(
            'Acesso negado: Apenas funcionários, administradores, gerentes ou vendedores podem acessar o App.'
          );
          setLoading(false);
          return;
        }

        sessionStorage.setItem('tipo_usuario', tipo_usuario || '');
        sessionStorage.setItem('nfce', nfce || 'NAO');

        if (!isSuperAdmin) {
          let situacao = (situacao_empresa || '').toUpperCase();
          if (situacao === 'INATIVO' || situacao === 'SUSPENSO') {
            return forceLogout('Empresa bloqueada. Entre em contato com o suporte.');
          }
        }

        const listaFinal = listaPermissoes || [];
        sessionStorage.setItem('sistemacpermissoes', JSON.stringify(listaFinal));
        setPermissoes(listaFinal);

        sessionStorage.setItem('cod_empresa', cod_empresa || cod_empresa_selecionada);
        sessionStorage.setItem('cod_usuario', cod_usuario);
        sessionStorage.setItem('sistemacemail', email);
        sessionStorage.setItem('sistemacefornecedor', isSupplier ? 'sim' : 'nao');
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('login_origem', lembrar ? 'app-lembrar' : 'app');
        sessionStorage.setItem('funcao_funcionario', funcao_funcionario || '');

        // Persistência em localStorage para "Manter conectado"
        if (lembrar) {
          localStorage.setItem('app_token', token);
          localStorage.setItem('app_tipo_usuario', tipo_usuario || '');
          localStorage.setItem('app_cod_empresa', cod_empresa || cod_empresa_selecionada);
          localStorage.setItem('app_cod_usuario', cod_usuario);
          localStorage.setItem('app_sistemacemail', email);
          localStorage.setItem('app_sistemacefornecedor', isSupplier ? 'sim' : 'nao');
          localStorage.setItem('app_sistemacpermissoes', JSON.stringify(listaFinal));
          localStorage.setItem('app_login_origem', 'app-lembrar');
          localStorage.setItem('app_funcao_funcionario', funcao_funcionario || '');
          localStorage.setItem('app_nfce', nfce || 'NAO');
          const empresaSlug = sessionStorage.getItem('empresa');
          if (empresaSlug) localStorage.setItem('app_empresa', empresaSlug);
          if (cod_org) localStorage.setItem('app_cod_org', cod_org);
        }

        setToken(token, cod_org);
        toast.success('Login efetuado com sucesso');
        setIsAuthenticated(token);

        setTimeout(() => {
          window.location.href = '/app/mesas';
        }, 500);
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 403 && data?.cobranca) {
        setCobrancaBloqueada(data.cobranca);
      } else {
        toast.error(data?.message || 'Erro inesperado.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleLogout() {
    doLogout(sessionStorage.getItem('token'))
      .then(() => {
        forceLogout('Logout efetuado com sucesso.');
      })
      .catch(console.error);
  }

  return (
    <Context.Provider
      value={{
        isAuthenticated,
        handleLogin,
        handleLoginApp,
        handleLogout,
        loading,
        permissoes,
        temAcesso,
        usuarioConsumidor,
        setUsuarioConsumidor,
        enderecoUsuarioConsumidor,
        setEnderecoUsuarioConsumidor,
        searchingUrl,
        setSearchingUrl,
        forceLogout,
        cobrancaBloqueada,
        setCobrancaBloqueada,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export { AuthContext, Context };
